/**
 * German label mappings for ISFP enums
 * These mappings are used to display user-friendly labels in the UI
 */

import type { EnergyPriceUnit } from '../types/custom-assessment-input.types'
import type {
  BorderingType,
  DHWGeneratorType,
  EnergySource,
  HeatingGeneratorType,
  HeatingTransferType,
  Orientation,
  RoofOrientation,
  VentilationType,
} from '../types/isfp.types'

// Orientation Labels
export const orientationLabels: Record<Orientation, string> = {
  north: 'Nord',
  northeast: 'Nordost',
  east: 'Ost',
  southeast: 'Südost',
  south: 'Süd',
  southwest: 'Südwest',
  west: 'West',
  northwest: 'Nordwest',
}

// Roof Orientation Labels (includes horizontal)
export const roofOrientationLabels: Record<RoofOrientation, string> = {
  north: 'Nord',
  northeast: 'Nordost',
  east: 'Ost',
  southeast: 'Südost',
  south: 'Süd',
  southwest: 'Südwest',
  west: 'West',
  northwest: 'Nordwest',
  horizontal: 'Horizontal',
}

// Bordering Type Labels
export const borderingTypeLabels: Record<BorderingType, string> = {
  toSoil: 'An Erdreich',
  toOutsideAir: 'An Außenluft',
  toUnheated: 'Zu unbeheizt',
}

// Heating Generator Type Labels
export const heatingGeneratorTypeLabels: Record<HeatingGeneratorType, string> = {
  boiler: 'Kessel',
  standardBoiler: 'Standardkessel',
  lowTemperatureBoiler: 'Niedertemperaturkessel',
  condensingBoiler: 'Brennwertkessel',
  biomassBoiler: 'Biomassekessel',
  heatPump: 'Wärmepumpe',
  groundSourceHeatPump: 'Erdwärmepumpe',
  groundWaterWaterHeatPump: 'Grundwasser-Wärmepumpe',
  airWaterHeatPumpMonovalent: 'Luft-Wasser-Wärmepumpe (monovalent)',
  airWaterHeatPumpBivalent: 'Luft-Wasser-Wärmepumpe (bivalent)',
  airAirHeatPumpMonovalent: 'Luft-Luft-Wärmepumpe (monovalent)',
  airAirHeatPumpBivalent: 'Luft-Luft-Wärmepumpe (bivalent)',
  electricPanel: 'Elektroheizung',
  districtHeating: 'Fernwärme',
  vrf: 'VRF-System',
  chp: 'Blockheizkraftwerk (BHKW)',
  stove: 'Ofen',
  exhaustWaterHeatPump: 'Abwasser-Wärmepumpe',
  exhaustAirHeatPump: 'Abluft-Wärmepumpe',
  fuelCell: 'Brennstoffzelle',
  solarThermalSystem: 'Solarthermieanlage',
}

// DHW Generator Type Labels
export const dhwGeneratorTypeLabels: Record<DHWGeneratorType, string> = {
  combinedWithHeating: 'Kombiniert mit Heizung',
  boiler: 'Kessel',
  standardBoiler: 'Standardkessel',
  lowTemperatureBoiler: 'Niedertemperaturkessel',
  condensingBoiler: 'Brennwertkessel',
  biomassBoiler: 'Biomassekessel',
  heatPump: 'Wärmepumpe',
  groundSourceHeatPump: 'Erdwärmepumpe',
  groundWaterWaterHeatPump: 'Grundwasser-Wärmepumpe',
  airWaterHeatPumpMonovalent: 'Luft-Wasser-Wärmepumpe (monovalent)',
  airWaterHeatPumpBivalent: 'Luft-Wasser-Wärmepumpe (bivalent)',
  exhaustAirAirWaterHeatPump: 'Abluft-Wärmepumpe',
  districtHeating: 'Fernwärme',
  chp: 'Blockheizkraftwerk (BHKW)',
  electricFlowHeater: 'Elektrischer Durchlauferhitzer',
  gasFlowHeater: 'Gas-Durchlauferhitzer',
  dhwStation: 'Warmwasserstation',
  solarThermalSystem: 'Solarthermieanlage',
}

// Energy Source Labels
export const energySourceLabels: Record<EnergySource, string> = {
  heatingOil: 'Heizöl',
  bioOil: 'Bio-Öl',
  gas: 'Gas',
  bioGas: 'Biogas',
  liquefiedGas: 'Flüssiggas',
  firewood: 'Brennholz',
  pellets: 'Pellets',
  biomass: 'Biomasse',
  electricity: 'Strom',
  districtHeatHeatPlant: 'Fernwärme Heizwerk',
  districtHeatHeatPlantFossil: 'Fernwärme Heizwerk (fossil)',
  districtHeatHeatPlantRenewable: 'Fernwärme Heizwerk (erneuerbar)',
  districtHeatCHPFossil: 'Fernwärme KWK (fossil)',
  districtHeatCHPRenewable: 'Fernwärme KWK (erneuerbar)',
  mineralCoal: 'Steinkohle',
  brownCoal: 'Braunkohle',
  blackCoal: 'Schwarzkohle',
  solar: 'Solar',
  coke: 'Koks',
}

// Energy Price Unit Labels
export const energyPriceUnitLabels: Record<EnergyPriceUnit, string> = {
  ctPerKwh: 'ct/kWh',
  ctPerLiter: 'ct/l',
  ctPerCbm: 'ct/m³',
}

// Heating Transfer Type Labels
export const heatingTransferTypeLabels: Record<HeatingTransferType, string> = {
  radiators: 'Heizkörper',
  floorHeating: 'Fußbodenheizung',
  fanCoil: 'Gebläsekonvektor',
  wallHeating: 'Wandheizung',
}

// Ventilation Type Labels
export const ventilationTypeLabels: Record<VentilationType, string> = {
  naturalVentilation: 'Freie Lüftung',
  other: 'Andere',
}

// Helper Types for Options
export interface SelectOption<T = string> {
  value: T
  label: string
}

// Helper Functions to Get Options Arrays

/**
 * Get orientation options for select components
 */
export const getOrientationOptions = (): SelectOption<Orientation>[] => {
  return Object.entries(orientationLabels).map(([value, label]) => ({
    value: value as Orientation,
    label,
  }))
}

/**
 * Get roof orientation options for select components (includes horizontal)
 */
export const getRoofOrientationOptions = (): SelectOption<RoofOrientation>[] => {
  return Object.entries(roofOrientationLabels).map(([value, label]) => ({
    value: value as RoofOrientation,
    label,
  }))
}

/**
 * Get bordering type options for select components
 */
export const getBorderingTypeOptions = (): SelectOption<BorderingType>[] => {
  return Object.entries(borderingTypeLabels).map(([value, label]) => ({
    value: value as BorderingType,
    label,
  }))
}

/**
 * Get heating generator type options for select components
 */
export const getHeatingGeneratorTypeOptions = (): SelectOption<HeatingGeneratorType>[] => {
  return Object.entries(heatingGeneratorTypeLabels).map(([value, label]) => ({
    value: value as HeatingGeneratorType,
    label,
  }))
}

/**
 * Get DHW generator type options for select components
 */
export const getDHWGeneratorTypeOptions = (): SelectOption<DHWGeneratorType>[] => {
  return Object.entries(dhwGeneratorTypeLabels).map(([value, label]) => ({
    value: value as DHWGeneratorType,
    label,
  }))
}

/**
 * Get energy source options for select components
 */
export const getEnergySourceOptions = (): SelectOption<EnergySource>[] => {
  return Object.entries(energySourceLabels).map(([value, label]) => ({
    value: value as EnergySource,
    label,
  }))
}

/**
 * Get heating transfer type options for select components
 */
export const getHeatingTransferTypeOptions = (): SelectOption<HeatingTransferType>[] => {
  return Object.entries(heatingTransferTypeLabels).map(([value, label]) => ({
    value: value as HeatingTransferType,
    label,
  }))
}

/**
 * Get ventilation type options for select components
 */
export const getVentilationTypeOptions = (): SelectOption<VentilationType>[] => {
  return Object.entries(ventilationTypeLabels).map(([value, label]) => ({
    value: value as VentilationType,
    label,
  }))
}

// Helper Functions to Get Single Labels

/**
 * Get German label for orientation value
 */
export const getOrientationLabel = (value: Orientation): string => {
  return orientationLabels[value] || value
}

/**
 * Get German label for roof orientation value (includes horizontal)
 */
export const getRoofOrientationLabel = (value: RoofOrientation): string => {
  return roofOrientationLabels[value] || value
}

/**
 * Get German label for bordering type value
 */
export const getBorderingTypeLabel = (value: BorderingType): string => {
  return borderingTypeLabels[value] || value
}

/**
 * Get German label for heating generator type value
 */
export const getHeatingGeneratorTypeLabel = (value: HeatingGeneratorType): string => {
  return heatingGeneratorTypeLabels[value] || value
}

/**
 * Get German label for DHW generator type value
 */
export const getDHWGeneratorTypeLabel = (value: DHWGeneratorType): string => {
  return dhwGeneratorTypeLabels[value] || value
}

/**
 * Get German label for energy source value
 */
export const getEnergySourceLabel = (value: EnergySource): string => {
  return energySourceLabels[value] || value
}

/**
 * Get German label for heating transfer type value
 */
export const getHeatingTransferTypeLabel = (value: HeatingTransferType): string => {
  return heatingTransferTypeLabels[value] || value
}

/**
 * Get German label for ventilation type value
 */
export const getVentilationTypeLabel = (value: VentilationType): string => {
  return ventilationTypeLabels[value] || value
}

/**
 * Get label for energy price unit value
 */
export const getEnergyPriceUnitLabel = (value: EnergyPriceUnit): string => {
  return energyPriceUnitLabels[value] || value
}
